import { useEffect } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { PLATFORM_NAME, SITE_ORIGIN } from "@/config/brand";

/**
 * Lightweight SEO tag manager. Sets <title>, meta description, canonical, and
 * essential Open Graph / Twitter tags directly on document.head. Avoids adding
 * a helmet dependency; safe to use in any page component.
 *
 * Props:
 *   title       — full <title> for the page (required)
 *   description — meta description (required)
 *   path        — path portion of canonical URL (optional; defaults to current)
 *   image       — Open Graph image URL (optional; falls back to Website Settings og_image_url)
 */

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SEO({ title, description, path, image }) {
  const settings = useSiteSettings();
  const siteName = PLATFORM_NAME;
  const configuredOg = (settings.og_image_url || "").trim();

  useEffect(() => {
    const finalTitle = title ? `${title} · ${siteName}` : siteName;
    document.title = finalTitle;

    const currentPath =
      path ||
      (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonical = `${SITE_ORIGIN}${currentPath}`;
    const rawImage = image || configuredOg || `${SITE_ORIGIN}/og-collectorup.jpg`;
    // Ensure og:image is always an absolute production URL. If a caller
    // passes a relative asset path (e.g. "/textures/foo.webp"), prefix it
    // with SITE_ORIGIN so social crawlers don't receive a Preview host.
    const ogImage = /^https?:\/\//i.test(rawImage) ? rawImage : `${SITE_ORIGIN}${rawImage.startsWith("/") ? rawImage : `/${rawImage}`}`;

    upsertMeta("name", "description", description);
    upsertLink("canonical", canonical);

    upsertMeta("property", "og:site_name", siteName);
    upsertMeta("property", "og:title", finalTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:image:secure_url", ogImage);
    upsertMeta("property", "og:image:type", "image/jpeg");
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:image:alt", title
      ? `${title} — CollectorUp`
      : "CollectorUp logo — Panini FIFA World Cup 2026 sticker exchange");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", finalTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);
    upsertMeta("name", "twitter:image:alt", title
      ? `${title} — CollectorUp`
      : "CollectorUp logo — Panini FIFA World Cup 2026 sticker exchange");
  }, [title, description, path, image, siteName, configuredOg]);

  return null;
}
